import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Clock,
  Loader2,
  RefreshCw,
  TrendingUp,
  Store,
  Truck,
  Zap,
  Star,
  Utensils,
  LayoutDashboard,
  Flame,
  EyeOff,
  ShoppingBag,
} from "lucide-react";
import api from "../../api/api";
import * as S from "./styles";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ active: [], history: [] });
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchData = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const [resOrders, resHistory] = await Promise.all([
        api.get("/orders"),
        api.get("/orders/completed"),
      ]);
      setData({
        active: resOrders.data || [],
        history: resHistory.data || [],
      });
    } catch (error) {
      toast.error("Erro na sincronização de dados operacional.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const dataTimer = setInterval(() => fetchData(true), 30000);
    const clockTimer = setInterval(() => setCurrentTime(new Date()), 60000);

    return () => {
      clearInterval(dataTimer);
      clearInterval(clockTimer);
    };
  }, [fetchData]);

  const todayStr = useMemo(() => new Date().toLocaleDateString("pt-BR"), []);

  const todayStats = useMemo(() => {
    const all = [...data.active, ...data.history];
    const ordersToday = all.filter(
      (o) => new Date(o.created_at).toLocaleDateString("pt-BR") === todayStr,
    );

    return {
      orderCount: ordersToday.length,
      delivered: ordersToday.filter((o) =>
        ["finalizado", "entregue", "concluido"].includes(
          o.status?.toLowerCase(),
        ),
      ).length,
      canceled: ordersToday.filter(
        (o) => o.status?.toLowerCase() === "cancelado",
      ).length,
      preparing: data.active.filter(
        (o) => o.status?.toLowerCase() === "preparando",
      ).length,
      pending: data.active.filter((o) => o.status?.toLowerCase() === "pendente")
        .length,
    };
  }, [data, todayStr]);

  const getTopProducts = useCallback((orders) => {
    const counts = {};
    orders.forEach((order) => {
      order.items?.forEach((item) => {
        const name = item.product_name || item.name || "Produto Indefinido";
        counts[name] = (counts[name] || 0) + (item.quantity || 1);
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, []);

  const topToday = useMemo(() => {
    const todayOrders = [...data.active, ...data.history].filter(
      (o) => new Date(o.created_at).toLocaleDateString("pt-BR") === todayStr,
    );
    return getTopProducts(todayOrders);
  }, [data, todayStr, getTopProducts]);

  const topAllTime = useMemo(
    () => getTopProducts([...data.active, ...data.history]),
    [data, getTopProducts],
  );

  const calculateWaitTime = (createdAt) => {
    if (!createdAt) return 0;
    const start = new Date(createdAt);
    const diffInMs = currentTime - start;
    const diffInMins = Math.floor(diffInMs / 60000);
    return diffInMins < 0 ? 0 : diffInMins;
  };

  if (loading)
    return (
      <S.LoadingWrapper>
        <Loader2 size={40} className="spinner" />
        <p>Sincronizando Sala de Comando...</p>
      </S.LoadingWrapper>
    );

  return (
    <S.Container>
      <S.BusinessHeader>
        <div className="title-area">
          <div className="breadcrumb">
            <LayoutDashboard size={14} /> Sala de Comando
          </div>
          <h1>Painel de Controle</h1>
          <p>
            Operação em tempo real: <strong>{todayStr}</strong>
          </p>
        </div>

        <div className="header-actions">
          <button className="sync-button" onClick={() => fetchData()}>
            <RefreshCw size={16} /> Sincronizar
          </button>
        </div>
      </S.BusinessHeader>

      <S.EnterpriseGrid>
        <S.MetricCard>
          <div className="card-top">
            <div
              className="icon"
              style={{ background: "#fff7ed", color: "#f97316" }}
            >
              <Flame size={20} />
            </div>
            <div className="badge-live">LIVE</div>
          </div>
          <div className="card-body">
            <label>Na Cozinha</label>
            <h3>
              {todayStats.preparing} <small>pedidos</small>
            </h3>
            <div className="progress-bar-bg">
              <div
                className={`progress-fill ${todayStats.preparing > 8 ? "critical" : ""}`}
                style={{
                  width: `${Math.min((todayStats.preparing / 12) * 100, 100)}%`,
                }}
              ></div>
            </div>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <div className="card-top">
            <div
              className="icon"
              style={{ background: "#eff6ff", color: "#3b82f6" }}
            >
              <Clock size={20} />
            </div>
            <div className="trend">Aguardando</div>
          </div>
          <div className="card-body">
            <label>Pendentes</label>
            <h3>
              {todayStats.pending} <small>na fila</small>
            </h3>
            <p className="sub-text">Aguardando aceite/preparo</p>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <div className="card-top">
            <div
              className="icon"
              style={{ background: "#f0fdf4", color: "#16a34a" }}
            >
              <Truck size={20} />
            </div>
            <div className="trend positive">Eficiência</div>
          </div>
          <div className="card-body">
            <label>Concluídos</label>
            <h3>
              {todayStats.delivered} <small>hoje</small>
            </h3>
            <p className="sub-text">Pedidos finalizados com sucesso</p>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <div className="card-top">
            <div
              className="icon"
              style={{ background: "#fef2f2", color: "#ef4444" }}
            >
              <EyeOff size={20} />
            </div>
            <div className="trend negative">Atenção</div>
          </div>
          <div className="card-body">
            <label>Cancelados</label>
            <h3 style={{ color: "#ef4444" }}>{todayStats.canceled}</h3>
            <p className="sub-text">Pedidos perdidos hoje</p>
          </div>
        </S.MetricCard>

        <S.MetricCard>
          <div className="card-top">
            <div
              className="icon"
              style={{ background: "#f8fafc", color: "#64748b" }}
            >
              <ShoppingBag size={20} />
            </div>
            <Zap size={16} fill="#eab308" color="#eab308" />
          </div>
          <div className="card-body">
            <label>Volume Total</label>
            <h3>{todayStats.orderCount}</h3>
            <p className="sub-text">Total de tickets abertos hoje</p>
          </div>
        </S.MetricCard>

        <S.QuickActionsBox>
          <h4>Atalhos Operacionais</h4>
          <div className="action-buttons">
            <button
              className="btn-primary"
              onClick={() => navigate("/OrderBoard")}
            >
              <Flame size={16} /> Abrir KDS
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate("/NewOrder")}
            >
              <Utensils size={16} /> Novo Pedido
            </button>
          </div>
        </S.QuickActionsBox>
      </S.EnterpriseGrid>

      <S.MainDashboardGrid>
        <S.SectionBox>
          <div className="section-header">
            <h3>
              <Clock size={18} /> Monitor de Pedidos em Aberto
            </h3>
          </div>
          <S.TableWrapper>
            <table className="enterprise-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Canal</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Tempo de Espera</th>
                </tr>
              </thead>
              <tbody>
                {data.active.length > 0 ? (
                  data.active
                    .filter((order) =>
                      ["pendente", "preparando", "pronto"].includes(
                        order.status?.toLowerCase(),
                      ),
                    )
                    .slice(0, 10)
                    .map((order) => {
                      const minutes = calculateWaitTime(order.created_at);

                      return (
                        <tr key={order.id}>
                          <td className="id-cell">
                            <strong>#{order.id.toString().slice(-4)}</strong>
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {order.type?.toLowerCase() === "delivery" ? (
                                <Truck size={16} color="#3b82f6" />
                              ) : (
                                <Store size={16} color="#8b5cf6" />
                              )}
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  color: "#64748b",
                                }}
                              >
                                {order.type?.toUpperCase()}
                              </span>
                            </div>
                          </td>
                          <td>
                            <S.EnterpriseBadge status={order.status}>
                              {order.status === "preparando"
                                ? "NA CHAPA"
                                : order.status === "pendente"
                                  ? "AGUARDANDO"
                                  : order.status?.toUpperCase() || "concluido"}
                            </S.EnterpriseBadge>
                          </td>
                          <td
                            className="time-cell"
                            style={{ textAlign: "right" }}
                          >
                            <span
                              style={{
                                fontWeight: "bold",
                                color:
                                  minutes >= 30
                                    ? "#ef4444"
                                    : minutes >= 15
                                      ? "#f97316"
                                      : "#22c55e",
                                backgroundColor:
                                  minutes >= 15 ? "#fff5ed" : "transparent",
                                padding: "4px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              {minutes} min
                            </span>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "4rem" }}
                    >
                      <div
                        style={{
                          opacity: 0.3,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <ShoppingBag size={48} />
                        <p style={{ marginTop: "10px" }}>
                          Cozinha limpa! Sem pedidos ativos.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </S.TableWrapper>
        </S.SectionBox>

        <S.SidePanel>
          <S.InsightCard>
            <h4>
              <Star size={16} color="#eab308" fill="#eab308" /> Mais Vendidos
              (Hoje)
            </h4>
            <div className="ranking-enterprise">
              {topToday.map(([name, count], index) => (
                <div className="rank-row" key={index}>
                  <div className="rank-info">
                    <span>{name}</span>
                    <strong>{count}x</strong>
                  </div>
                  <div className="rank-bar-mini">
                    <div
                      style={{
                        width: `${(count / (topToday[0][1] || 1)) * 100}%`,
                        background: "var(--primary)",
                      }}
                    ></div>
                  </div>
                </div>
              ))}
              {topToday.length === 0 && (
                <p className="empty-text">Sem dados hoje.</p>
              )}
            </div>
          </S.InsightCard>

          <S.InsightCard>
            <h4>
              <TrendingUp size={16} color="#3b82f6" /> Histórico de Popularidade
            </h4>
            <div className="ranking-enterprise">
              {topAllTime.map(([name, count], index) => (
                <div className="rank-row" key={index}>
                  <div className="rank-info">
                    <span>{name}</span>
                    <strong>{count}x</strong>
                  </div>
                  <div className="rank-bar-mini">
                    <div
                      style={{
                        width: `${(count / (topAllTime[0][1] || 1)) * 100}%`,
                        background: "#3b82f6",
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </S.InsightCard>
        </S.SidePanel>
      </S.MainDashboardGrid>
    </S.Container>
  );
};

export default Dashboard;
